import { Box, Button, ButtonGroup, Container, Divider, Flex, GridItem, Heading, HStack, SimpleGrid, Tag, Td, Text, Tr, useColorModeValue } from "@chakra-ui/react"
import { Form, Icon, IconButton, Searchable, Table } from "components"
import { Moment } from "config"
import { Mask } from "config"
import { Enums } from "config"
import { useOrdersList, useScreens, useUser } from "hooks"
import { useEffect, useState } from "react"

const REGIONS = {
    "Norte":        ["AC", "AM", "AP", "PA", "RO", "RR", "TO"],
    "Nordeste":     ["AL", "BA", "CE", "MA", "PB", "PE", "PI", "RN", "SE"],
    "Centro-Oeste": ["DF", "GO", "MS", "MT"],
    "Sudeste":      ["ES", "MG", "RJ", "SP"],
    "Sul":          ["PR", "SC", "RS"],
}

const STATE_LABELS = {
    AC: "Acre",                AP: "Amapá",               AM: "Amazonas",
    PA: "Pará",                RO: "Rondônia",             RR: "Roraima",
    TO: "Tocantins",           AL: "Alagoas",              BA: "Bahia",
    CE: "Ceará",               MA: "Maranhão",             PB: "Paraíba",
    PE: "Pernambuco",          PI: "Piauí",                RN: "Rio Grande do Norte",
    SE: "Sergipe",             DF: "Distrito Federal",     GO: "Goiás",
    MS: "Mato Grosso do Sul",  MT: "Mato Grosso",          ES: "Espírito Santo",
    MG: "Minas Gerais",        RJ: "Rio de Janeiro",       SP: "São Paulo",
    PR: "Paraná",              SC: "Santa Catarina",       RS: "Rio Grande do Sul",
}

function FilterLabel({ children }) {
    const color = useColorModeValue("gray.400", "gray.500")
    return (
        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase" letterSpacing="widest" color={color} mb={1}>
            {children}
        </Text>
    )
}

export default function Orders(){

    const { setOrder } = useScreens()
    const user = useUser()
    const myWarehouse = user.data?.warehouse?.find(w => !w.is_matriz)
    const initial = [Moment().subtract('months', 3).toDate(), Moment().toDate()]

    const [ period, setPeriod ]           = useState(initial)
    const [ page, setPage ]               = useState(1)
    const [ term, setTerm ]               = useState('')
    const [ item, setItem ]               = useState('')
    const [ type, setType ]               = useState('')
    const [ status, setStatus ]           = useState('')
    const [ client, setClient ]           = useState('')
    const [ distributor, setDistributor ] = useState('')
    const [ orderBy, setOrderBy ]         = useState('created_at')
    const [ showPendingOrders, setShowPendingOrders ] = useState(false)
    const [ region, setRegion ]           = useState('')
    const [ state, setState ]             = useState('')
    // Incrementado ao limpar — força remount dos selects não-controlados
    const [ filterKey, setFilterKey ]     = useState(0)

    const stateOptions = region
        ? REGIONS[region].map(uf => ({ label: `${uf} — ${STATE_LABELS[uf]}`, value: uf }))
        : []

    const orders = useOrdersList(page, term, type, status, client, distributor, orderBy, period, item, state)

    const hasActiveFilters = !!(term || item || type || status || client || region || state || showPendingOrders)

    const clearFilters = () => {
        setTerm('')
        setItem('')
        setType('')
        setStatus('')
        setClient('')
        setRegion('')
        setState('')
        setOrderBy('created_at')
        setShowPendingOrders(false)
        setPeriod(initial)
        if (!myWarehouse?.id) setDistributor('')
        setPage(1)
        setFilterKey(k => k + 1)
    }

    useEffect(() => { setPage(1) }, [term, type, status, distributor, client, orderBy, item])
    useEffect(() => { if (myWarehouse?.id) setDistributor(myWarehouse.id) }, [myWarehouse?.id])
    useEffect(() => { setState('') }, [region])
    useEffect(() => { setPage(1) }, [region, state])

    const filterBg        = useColorModeValue("gray.50",  "gray.700")
    const filterBorder    = useColorModeValue("gray.100", "gray.600")
    const filterLabelC    = useColorModeValue("gray.400", "gray.500")
    const pendencyTextC   = useColorModeValue("gray.500", "gray.400")
    const dividerC        = useColorModeValue("gray.200", "gray.600")
    const clearBg         = useColorModeValue("red.50",   "rgba(254,178,178,0.1)")
    const clearBorder     = useColorModeValue("red.200",  "rgba(254,178,178,0.2)")
    const clearHoverBg    = useColorModeValue("red.100",  "rgba(254,178,178,0.18)")
    const clearActiveBg   = useColorModeValue("red.200",  "rgba(254,178,178,0.25)")

    return(
    <Container maxW="90%" mt={10}>

        <Flex justifyContent="space-between" alignItems="center" mb={6}>
            <Heading children={myWarehouse ? `Pedidos de ${myWarehouse.name}` : 'Pedidos da Matriz'} />
            <ButtonGroup size="xs">
                <IconButton icon="refresh" isLoading={orders.isFetching} onClick={orders.refetch} />
            </ButtonGroup>
        </Flex>

        {/* ── Painel de filtros ─────────────────────────────────── */}
        <Box
            bg={filterBg}
            border="1px solid"
            borderColor={filterBorder}
            borderRadius="xl"
            p={4}
            mb={4}
        >
            {/* Header */}
            <Flex justify="space-between" align="center" mb={3}>
                <HStack spacing={2}>
                    <Icon name="search" boxSize={3.5} color={filterLabelC} />
                    <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="widest" color={filterLabelC}>
                        Filtros
                    </Text>
                    {hasActiveFilters && (
                        <Box w="6px" h="6px" borderRadius="full" bg="#008AAC" flexShrink={0} />
                    )}
                </HStack>

                {hasActiveFilters && (
                    <Button
                        size="sm"
                        onClick={clearFilters}
                        borderRadius="full"
                        px={4}
                        h="28px"
                        fontSize="xs"
                        fontWeight="semibold"
                        bg={clearBg}
                        color="red.500"
                        border="1px solid"
                        borderColor={clearBorder}
                        _hover={{ bg: clearHoverBg, borderColor: "red.300" }}
                        _active={{ bg: clearActiveBg }}
                        leftIcon={<Icon name="close" boxSize={3} />}
                        transition="all 0.15s"
                    >
                        Limpar filtros
                    </Button>
                )}
            </Flex>

            {/* Busca */}
            <SimpleGrid columns={{ base: 1, md: 4 }} gap={2} mb={4}>
                <GridItem colSpan={{ base: 1, md: 3 }}>
                    <Searchable value={term} setValue={setTerm} placeholder="Pesquisar por ID, Nome do cliente ou Código do pedido..." />
                </GridItem>
                <GridItem>
                    <Searchable value={item} setValue={setItem} placeholder="Produto do pedido..." />
                </GridItem>
            </SimpleGrid>

            {/* Escopo */}
            <FilterLabel>Escopo</FilterLabel>
            <SimpleGrid key={`scope-${filterKey}`} columns={{ base: 2, md: 4 }} gap={2} mb={4}>
                { myWarehouse
                    ? <Form.Select value={myWarehouse.id} onChange={e => setDistributor(e.target.value)} placeholder={myWarehouse.name} options={[{ label: myWarehouse.name, value: myWarehouse.id }]} />
                    : <Form.Select value={distributor} onChange={e => setDistributor(e.target.value)} placeholder="Núcleos" options={[{ label: 'Matriz', value: 'matriz' }, { label: 'Todos', value: 'all' }]} />
                }
                <Form.Select value={client} onChange={e => setClient(e.target.value)} placeholder="Núcleos/Consultores" options={[{ label: 'Todos', value: '' }, { label: 'Pedidos de Consultores', value: 'consultant' }, { label: 'Pedidos de Núcleos', value: 'distributor' }]} />
                <Form.Select value={type} onChange={e => setType(e.target.value)} placeholder="Todos os tipos" options={[{ label: 'Todos os tipos', value: '' }, { label: 'Produtos', value: 'products' }, { label: 'Voucher', value: 'vouchers' }, { label: 'Adesão', value: 'adhesion' }]} />
                <Form.Select value={status} onChange={e => setStatus(e.target.value)} placeholder="Todos os status" options={[{ label: 'Todos os status', value: '' }, { label: 'Aprovado', value: Enums.OrderStatus.APPROVED }, { label: 'Cancelado', value: Enums.OrderStatus.CANCELED }, { label: 'Em Análise', value: Enums.OrderStatus.ANALYZING }, { label: 'Montagem', value: Enums.OrderStatus.MONTAGE }, { label: 'Cotação', value: Enums.OrderStatus.QUOTATION }, { label: 'Nota Fiscal', value: Enums.OrderStatus.FISCAL }, { label: 'Ag. retirada', value: Enums.OrderStatus.WITHDRAWAL }, { label: 'Ag. envio', value: Enums.OrderStatus.WAITING_SHIPPING }, { label: 'Em Trânsito', value: Enums.OrderStatus.SHIPPING }, { label: 'Concluído', value: Enums.OrderStatus.DONE }]} />
            </SimpleGrid>

            {/* Localização, Ordenação & Pendências */}
            <FilterLabel>Localização &amp; Ordenação</FilterLabel>
            <Flex key={`loc-${filterKey}`} gap={2} align="center" flexWrap={{ base: "wrap", md: "nowrap" }}>
                <Box flex={1} minW="130px">
                    <Form.Select value={region} onChange={e => setRegion(e.target.value)} placeholder="Todas as regiões" options={Object.keys(REGIONS).map(r => ({ label: r, value: r }))} />
                </Box>
                <Box flex={1} minW="160px">
                    <Form.Select key={`state-${region}-${filterKey}`} value={state} onChange={e => setState(e.target.value)} placeholder={region ? "Todos os estados" : "Selecione uma região"} isDisabled={!region} options={stateOptions} />
                </Box>
                <Box flex={1} minW="150px">
                    <Form.Select value={orderBy} onChange={e => setOrderBy(e.target.value)} placeholder="Ordenar por" options={[{ label: 'Data do pedido', value: 'created_at' }, { label: 'Data de aprovação', value: 'approved_at' }]} />
                </Box>
                <Box flexShrink={0}>
                    <Form.Interval initial={initial} onSelect={setPeriod} />
                </Box>

                <Divider orientation="vertical" h="32px" borderColor={dividerC} flexShrink={0} display={{ base: "none", md: "block" }} />

                <HStack spacing={2} flexShrink={0}>
                    <Text fontSize="sm" color={pendencyTextC} whiteSpace="nowrap">Pendências</Text>
                    <Form.Switch value={showPendingOrders} onChange={e => setShowPendingOrders(e.target.checked)} />
                </HStack>
            </Flex>
        </Box>

        <Table
            columns={[
                { children: '', isNumeric: true },
                { children: 'Detalhes' },
                { children: 'Valor', isNumeric: true },
                { children: 'Cliente' },
                { children: 'Data', isNumeric: true },
            ]}
            setPage={setPage}
            meta={orders.data?.meta}
        >
            { orders.data?.data?.filter(item => {
                return showPendingOrders ? item?.parent_order_id !== null : true
            }).map(item => (
                <Tr key={item.id} cursor="pointer" _hover={{ backgroundColor: 'rgba(0,0,0,0.03)' }} onClick={() => setOrder(item)}>
                    <Td isNumeric>{item.cod}</Td>
                    <Td>
                        <HStack spacing={2}>
                            <Tag size="sm" colorScheme={Enums.OrderStatus.data(item.status).colorScheme} children={item.status_label} />
                            { item.type === 3 && <Tag size="sm" children="Adesão" colorScheme="blue" />}
                            { item.type === 4 && <Tag size="sm" children="Vouchers" colorScheme="purple" />}
                            { item.shipping_type === 1 && <Tag size="sm" children="Entrega" />}
                            { item.shipping_type === 2 && <Tag size="sm" children="Retirada" />}
                            { item?.parent_order_id && <Tag size="sm" children="Pedidos de Pendência" colorScheme="orange" />}
                        </HStack>
                    </Td>
                    <Td fontWeight="bold" fontSize="xs" textAlign="right">R$ {Mask.price(item.total_amount)}</Td>
                    <Td fontSize="xs">
                        <HStack>
                            { !!item.consultant_id && <Tag size="sm" children="Consultor" />}
                            { !!item.distributor_to_id && <Tag size="sm" children="Núcleo" />}
                            <Text>{item.distributor_to?.name ?? item.consultant?.customer?.name}</Text>
                        </HStack>
                    </Td>
                    <Td isNumeric fontSize="xs">{Mask.dateTime((orderBy === 'created_at') ? item?.created_at : item?.approved_at)}</Td>
                </Tr>
            )) }
        </Table>
    </Container>
    )
}
